import { useEffect, useMemo, useRef, useState } from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useFilter } from "../../context/FilterContext";
import axios from "axios";
import { FaSearch, FaTimes } from "react-icons/fa";

const STORAGE_KEYS = {
    food: "savedPhysicalCount_food",
    beverage: "savedPhysicalCount_beverage",
};

const getStorageKey = (type) =>
    STORAGE_KEYS[type?.toLowerCase()] || STORAGE_KEYS.food;

const AddPhysicalCount = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { inventoryData, selectedOutlet, accessToken } = location.state || {};
    const token = localStorage.getItem('token');
    const inputRefs = useRef([]);
    const groupRefs = useRef({});
    const itemRefs = useRef({});
    const { filter, setFilter } = useFilter();
    const [isLoading, setIsLoading] = useState(false);
    const [physicalItems, setPhysicalItems] = useState([]);
    const [itemCosts, setItemCosts] = useState({});

    // ── Search state ─────────────────────────────────────────────────────────
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);

    useEffect(() => {
        if (accessToken !== "a7fK2pX9Zd") {
            navigate('/inventorycalculation');
        }
    }, [accessToken, navigate]);

    useEffect(() => {
        if (inventoryData?.summary) {
            const savedFood =
                JSON.parse(localStorage.getItem(STORAGE_KEYS.food)) || [];
            const savedBeverage =
                JSON.parse(localStorage.getItem(STORAGE_KEYS.beverage)) || [];

            const items = [];

            Object.entries(inventoryData.summary).forEach(([category, groups]) => {
                const savedList =
                    category?.toLowerCase() === "food" ? savedFood : savedBeverage;

                Object.entries(groups).forEach(([groupName, itemsArray]) => {
                    itemsArray.forEach((item) => {
                        const savedItem = savedList.find(
                            (s) => s.item_name === item.name
                        );

                        items.push({
                            id: `${item.name}-${item.primary_unit}`,
                            item_name: item.name,
                            category: category,
                            groupName: groupName,

                            primaryQty: savedItem ? String(savedItem.primaryQty) : "",
                            secondaryQty: savedItem
                                ? String(savedItem.secondaryQty)
                                : "",

                            uom: item.uom,
                            new_uom: item.new_uom,
                            primary_unit: item.primary_unit,
                            secondary_unit: item.secondary_unit,
                            unitdefinition: item.unitdefinition,
                            closing_balance: item.closing_balance,
                        });
                    });
                });
            });

            setPhysicalItems(items);
        }
    }, [inventoryData]);

    const fetchPhysicalCostById = (id) => {
        const latestItem = physicalItems.find(i => i.id === id);
        if (latestItem) {
            fetchPhysicalCost(latestItem);
        }
    };

    const fetchPhysicalCost = async (item) => {
        try {
            const physicalCount = computeFinalQuantity(item, unitMappings);
            if (!physicalCount || physicalCount <= 0) return;

            const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/calculate-physical-cost`, {
                token: token,
                item_name: item.item_name,
                outlet: selectedOutlet,
                costcenter: filter?.toLowerCase() === "food" ? "Kitchen" : "Bar",
                physical_count: physicalCount
            });

            const data = response.data || {};
            setItemCosts(prev => ({
                ...prev,
                [item.id]: data?.physical_cost || 0
            }));
        } catch (error) {
            console.error("Error calculating physical cost:", error);
        }
    };

    const handlePrimaryChange = (id, value) => {
        setPhysicalItems(prevItems =>
            prevItems.map(item =>
                item.id === id ? { ...item, primaryQty: value === "" ? "" : value } : item
            )
        );
    };

    const handleSecondaryChange = (id, value) => {
        setPhysicalItems(prevItems =>
            prevItems.map(item =>
                item.id === id ? { ...item, secondaryQty: value === "" ? "" : value } : item
            )
        );
    };

    function computeFinalQuantity(item, unitMappings) {
        const primaryQty = parseFloat(item.primaryQty) || 0;
        const secondaryQty = parseFloat(item.secondaryQty) || 0;
        const unitSize = parseFloat(item.unitdefinition.unit) || 1;
        const baseUom = (item.unitdefinition.uom || '').toLowerCase();
        const itemUom = (item.primary_unit || '').toLowerCase();
        const newUom = (item.secondary_unit || '').toLowerCase();

        const { kgVariations, gramVariations, literVariations, milliliterVariations } = unitMappings;
        const isMatch = (uom, variations) => variations.includes(uom);

        let primaryInBaseUnit, secondaryInBaseUnit;

        if (itemUom === baseUom) {
            primaryInBaseUnit = primaryQty;
        } else {
            primaryInBaseUnit = primaryQty * unitSize;
        }

        if (newUom === baseUom) {
            secondaryInBaseUnit = secondaryQty;
        } else {
            if (isMatch(baseUom, kgVariations) && isMatch(newUom, gramVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else if (isMatch(baseUom, literVariations) && isMatch(newUom, milliliterVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else {
                secondaryInBaseUnit = secondaryQty;
            }
        }

        const totalInBaseUnit = (primaryInBaseUnit + secondaryInBaseUnit) / unitSize;
        return totalInBaseUnit;
    }

    const unitMappings = {
        kgVariations: ['kg', 'kgs', 'kilogram', 'kilograms'],
        gramVariations: ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'],
        literVariations: ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'],
        milliliterVariations: ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls']
    };

    const handleSubmit = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save this physical count?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (!token || !selectedOutlet) {
                                toast.error("Please select an outlet");
                                return;
                            }

                            setIsLoading(true);
                            try {
                                const storageKey = getStorageKey(filter); // <-- separate key
                                const existing =
                                    JSON.parse(localStorage.getItem(storageKey)) || [];

                                const updatedExisting = existing.filter(
                                    (oldItem) => !filteredItems.some((f) => f.id === oldItem.id)
                                );

                                const newItems = filteredItems.map((item) => ({
                                    id: item.id,
                                    item_name: item.item_name,
                                    quantity: computeFinalQuantity(item, unitMappings),
                                    primaryQty: item.primaryQty,
                                    secondaryQty: item.secondaryQty,
                                }));

                                const finalData = [...updatedExisting, ...newItems];

                                localStorage.setItem(storageKey, JSON.stringify(finalData));

                                toast.success(
                                    `${filter === "food" ? "Food" : "Beverage"} physical count saved!`
                                );
                                navigate("/inventorycalculation");
                            } catch (error) {
                                console.error("Error saving physical count locally:", error);
                                toast.error("Error saving physical count");
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-physical-stocks'
            }
        );
    };

    const filteredItems = physicalItems.filter(
        item => item.category?.toLowerCase() === filter
    );

    // Group by groupName (category group), not item_name
    const groupedItems = filteredItems.reduce((acc, item) => {
        const group = item.groupName || "Uncategorized";
        if (!acc[group]) acc[group] = [];
        acc[group].push(item);
        return acc;
    }, {});

    // ── Build a flat searchable list whenever groupedItems changes ─────────────
    const searchableEntries = useMemo(() => {
        const entries = [];

        Object.entries(groupedItems).forEach(([groupName, items]) => {
            entries.push({ type: "group", label: groupName, groupName });

            items.forEach((item) => {
                entries.push({
                    type: "item",
                    label: item.item_name,
                    groupName,
                    itemKey: item.id,
                });
            });
        });

        return entries;
    }, [groupedItems]);

    // ── Run search on query change ───────────────────────────────────────────
    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (!query.trim()) {
            setSearchResults([]);
            setShowSearchDropdown(false);
            return;
        }

        const lowerQuery = query.toLowerCase();
        const matches = searchableEntries.filter((entry) =>
            entry.label.toLowerCase().includes(lowerQuery)
        );

        setSearchResults(matches.slice(0, 15));
        setShowSearchDropdown(true);
    };

    // ── Scroll to a group or a specific item row ─────────────────────────────
    const handleSelectSearchResult = (entry) => {
        setShowSearchDropdown(false);
        setSearchQuery(entry.label);

        if (entry.type === "group") {
            const el = groupRefs.current[entry.groupName];
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            return;
        }

        const groupEl = groupRefs.current[entry.groupName];
        if (groupEl) {
            groupEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        setTimeout(() => {
            const rowEl = itemRefs.current[entry.itemKey];
            if (rowEl) {
                rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
                rowEl.classList.add("bg-blue-100");
                setTimeout(() => rowEl.classList.remove("bg-blue-100"), 1500);
            }
        }, 300);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter" && searchResults.length > 0) {
            handleSelectSearchResult(searchResults[0]);
        } else if (e.key === "Escape") {
            setShowSearchDropdown(false);
        }
    };

    const clearSearch = () => {
        setSearchQuery("");
        setSearchResults([]);
        setShowSearchDropdown(false);
    };

    const shouldShowSecondary = (item) => {
        const primaryUnit = (item.primary_unit || '').toLowerCase().trim();
        const secondaryUnit = (item.secondary_unit || '').toLowerCase().trim();
        const defUom = (item.unitdefinition?.uom || '').toLowerCase().trim();

        if (!defUom) {
            return primaryUnit !== secondaryUnit;
        }

        return !(primaryUnit === secondaryUnit && secondaryUnit === defUom);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const currentIndex = inputRefs.current.indexOf(e.target);
            const nextInput = inputRefs.current[currentIndex + 1];
            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    const handlePrint = () => {
        const printWindow = window.open("", "_blank");

        const printGrouped = filteredItems.reduce((acc, item) => {
            const group = item.groupName || "Uncategorized";

            if (!acc[group]) acc[group] = [];

            acc[group].push(item);
            return acc;
        }, {});

        Object.keys(printGrouped).forEach((group) => {
            printGrouped[group].sort((a, b) =>
                (a.item_name || "")
                    .toLowerCase()
                    .localeCompare((b.item_name || "").toLowerCase())
            );
        });

        const groupTablesHtml = Object.entries(printGrouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([groupName, items]) => {
                const isSmallGroup = items.length <= 5;

                return `
                <div class="group-section ${isSmallGroup ? "keep-together" : ""}">
                    <div class="group-header">
                        <h3 class="group-title">${groupName}</h3>
                    </div>

                    <table>
                        <thead>
                            <tr>
                                <th>Item Name</th>
                                <th>Unit Definition</th>
                                <th>Primary</th>
                                <th>Secondary</th>
                            </tr>
                        </thead>

                        <tbody>
                            ${items
                        .map((item) => {
                            const showSecondary = shouldShowSecondary(item);

                            return `
                                <tr>
                                    <td>${item.item_name}</td>

                                    <td>
                                        ${item.unitdefinition?.unit || ""}
                                        (${item.unitdefinition?.uom || ""})
                                    </td>

                                    <td>
                                        <div class="qty-box">
                                            <span class="line"></span>
                                            <span>${item.primary_unit || ""}</span>
                                        </div>
                                    </td>

                                    <td>
                                        ${showSecondary
                                    ? `
                                        <div class="qty-box">
                                            <span class="line"></span>
                                            <span>${item.secondary_unit || ""}</span>
                                        </div>
                                    `
                                    : "-"
                                }
                                </td>
                            </tr>
                        `;
                        })
                        .join("")}
                        </tbody>
                    </table>
                </div>
            `;
            })
            .join("");

        const content = `
        <html>
        <head>
            <title>Physical Stock Count Sheet</title>

            <style>
                body {
                    font-family: Arial, sans-serif;
                    padding: 5px;
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 15px;
                    font-size: 14px;
                }

                .group-section {
                    margin-bottom: 24px;
                }

                .group-header {
                    page-break-after: avoid;
                    break-after: avoid;
                }

                .group-title {
                    font-size: 14px;
                    font-weight: bold;
                    margin-bottom: 6px;
                    padding: 4px 8px;
                    background-color: #e8e8e8;
                    border-left: 4px solid #555;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                }

                th,
                td {
                    border: 1px solid black;
                    padding: 8px;
                    text-align: center;
                }

                th {
                    background-color: #f2f2f2;
                }

                .qty-box {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 5px;
                }

                .line {
                    width: 60px;
                    border-bottom: 1px solid #000;
                    display: inline-block;
                }

                @media print {
                    .keep-together {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                    .group-header {
                        page-break-after: avoid;
                        break-after: avoid;
                    }

                    thead {
                        display: table-header-group;
                    }

                    tfoot {
                        display: table-footer-group;
                    }

                    table {
                        page-break-inside: auto;
                    }

                    tr {
                        page-break-inside: avoid;
                    }
                }
            </style>
        </head>

        <body>
            <div class="header">
                <div>Closing Stock Count for: ${filter?.toUpperCase()}</div>
                <div>Date: ____________</div>
            </div>

            ${groupTablesHtml}
        </body>
        </html>
    `;

        printWindow.document.write(content);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };

    // Shared table header
    const TableHeader = () => (
        <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Definition</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Primary Unit</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Secondary Unit</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
            </tr>
            <tr>
                <th></th>
                <th></th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Qty</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">UOM</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Qty</th>
                <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">UOM</th>
                <th></th>
            </tr>
        </thead>
    );

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-6xl p-3 mx-auto">
                    {token && selectedOutlet && (
                        <>
                            <div className="text-lg font-semibold mb-1">Physical Stock Count for {selectedOutlet}</div>

                            <div className="flex justify-between items-center mb-2.5">
                                <div className="flex gap-6">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            value="food"
                                            checked={filter === "food"}
                                            onChange={(e) => setFilter(e.target.value)}
                                        />
                                        Food
                                    </label>

                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            value="beverage"
                                            checked={filter === "beverage"}
                                            onChange={(e) => setFilter(e.target.value)}
                                        />
                                        Beverage
                                    </label>

                                    <div className="rounded-md border italic border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                                        <span className="font-medium">Note:</span> Only one type can be saved at a time.
                                    </div>
                                </div>

                                <button
                                    onClick={handlePrint}
                                    className="px-3 py-1.5 rounded-md text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md"
                                >
                                    Print Sheet
                                </button>
                            </div>

                            {/* ── Search bar ────────────────────────────────────────────── */}
                            <div className="relative mb-4 max-w-md">
                                <div className="relative">
                                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        onKeyDown={handleSearchKeyDown}
                                        onFocus={() => searchResults.length > 0 && setShowSearchDropdown(true)}
                                        placeholder="Search by item or group name..."
                                        className="w-full pl-9 pr-8 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={clearSearch}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                                        >
                                            <FaTimes />
                                        </button>
                                    )}
                                </div>

                                {showSearchDropdown && searchResults.length > 0 && (
                                    <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-72 overflow-y-auto tiny-scrollbar">
                                        {searchResults.map((entry, idx) => (
                                            <button
                                                key={`${entry.type}-${entry.label}-${idx}`}
                                                onClick={() => handleSelectSearchResult(entry)}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between gap-2 border-b last:border-b-0"
                                            >
                                                <span className={entry.type === "group" ? "font-semibold text-gray-700" : "text-gray-600"}>
                                                    {entry.label}
                                                </span>
                                                <span className="text-xs shrink-0">
                                                    {entry.type === "group" ? (
                                                        <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">Group</span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500">{entry.groupName}</span>
                                                    )}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {showSearchDropdown && searchQuery && searchResults.length === 0 && (
                                    <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg px-3 py-2 text-sm text-gray-500">
                                        No matches found
                                    </div>
                                )}
                            </div>

                            {/* Reset inputRefs before rendering all group tables */}
                            {(() => { inputRefs.current = []; })()}

                            <div className="space-y-6">
                                {Object.keys(groupedItems).length > 0 ? (
                                    Object.entries(groupedItems)
                                        .sort(([a], [b]) => a.localeCompare(b))
                                        .map(([groupName, items]) => (
                                            <div
                                                key={groupName}
                                                ref={(el) => (groupRefs.current[groupName] = el)}
                                            >
                                                {/* Group heading */}
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="h-5 w-1 bg-blue-500 rounded"></div>
                                                    <div className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                                        {groupName}
                                                    </div>
                                                </div>

                                                <div className="rounded-md border overflow-auto max-h-[500px] tiny-scrollbar">
                                                    <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                                                        <TableHeader />
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {[...items]
                                                                .sort((a, b) => a.item_name.localeCompare(b.item_name))
                                                                .map((item) => {
                                                                    const showSecondary = shouldShowSecondary(item);

                                                                    const missingUnitDef = !item.unitdefinition;
                                                                    const missingPrimaryUom = !item.primary_unit;
                                                                    const missingSecondaryUom = showSecondary && !item.secondary_unit;

                                                                    const hasError =
                                                                        missingUnitDef ||
                                                                        missingPrimaryUom ||
                                                                        missingSecondaryUom;

                                                                    const rowClass = hasError
                                                                        ? "bg-yellow-50 hover:bg-yellow-100"
                                                                        : "hover:bg-gray-100";

                                                                    return (
                                                                        <tr
                                                                            key={item.id}
                                                                            ref={(el) => {
                                                                                if (el) itemRefs.current[item.id] = el;
                                                                            }}
                                                                            className={`transition-colors duration-300 ${rowClass}`}
                                                                            title={hasError ? "This item has missing data (unit definition or UOM)" : undefined}
                                                                        >
                                                                            <td className="px-4 py-2 whitespace-nowrap">
                                                                                {item.item_name}
                                                                            </td>
                                                                            <td className="px-4 py-2">
                                                                                {item.unitdefinition?.unit} ({item.unitdefinition?.uom || 'N/A'})
                                                                            </td>

                                                                            <td className="px-4 py-2 border-r border-gray-200">
                                                                                <input
                                                                                    ref={(el) => {
                                                                                        if (el) inputRefs.current.push(el);
                                                                                    }}
                                                                                    type="number"
                                                                                    className="w-20 border rounded px-2 py-1"
                                                                                    value={item.primaryQty}
                                                                                    onChange={(e) => handlePrimaryChange(item.id, e.target.value)}
                                                                                    onKeyDown={handleKeyDown}
                                                                                    onBlur={() => fetchPhysicalCostById(item.id)}
                                                                                    step="any"
                                                                                    min="0"
                                                                                />
                                                                            </td>
                                                                            <td className="px-4 py-2">
                                                                                {item.primary_unit || "N/A"}
                                                                            </td>

                                                                            <td className="px-4 py-2 border-r border-gray-200">
                                                                                {showSecondary ? (
                                                                                    <input
                                                                                        ref={(el) => {
                                                                                            if (el) inputRefs.current.push(el);
                                                                                        }}
                                                                                        type="number"
                                                                                        className="w-20 border rounded px-2 py-1"
                                                                                        value={item.secondaryQty}
                                                                                        onChange={(e) => handleSecondaryChange(item.id, e.target.value)}
                                                                                        onKeyDown={handleKeyDown}
                                                                                        onBlur={() => fetchPhysicalCost(item)}
                                                                                        step="any"
                                                                                        min="0"
                                                                                    />
                                                                                ) : '-'}
                                                                            </td>
                                                                            <td className="px-4 py-2">
                                                                                {showSecondary ? (item.secondary_unit || "N/A") : '-'}
                                                                            </td>

                                                                            <td className="px-4 py-2">
                                                                                {itemCosts[item.id] ? itemCosts[item.id].toFixed(2) : "-"}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ))
                                ) : (
                                    <div className="rounded-md border px-3 py-4 text-center text-gray-500">
                                        No data available
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end mt-3">
                                <button
                                    onClick={handleSubmit}
                                    disabled={isLoading}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
                                >
                                    {isLoading ? 'Saving...' : 'Save Physical Count'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Footer />
        </div >
    );
};

export default AddPhysicalCount;